import { useEffect, useState, useCallback } from 'react';
import axios, { AxiosError } from 'axios';
import { getAccessToken } from '../authUtils';
import type {
  Group,
  Job,
  Entity,
  JobEntity,
  FolderItem,
  SearchResult,
  Task,
  TasksListResponse,
  LookupRow,
  LookupTable,
  GroupType,
} from '../types/crm';

interface AsyncState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
}

function initialState<T>(): AsyncState<T> {
  return { data: null, loading: false, error: null };
}

function errorMessage(err: unknown): string {
  if (err instanceof AxiosError) {
    const data = err.response?.data as { error?: string } | undefined;
    return data?.error || err.message;
  }
  return err instanceof Error ? err.message : String(err);
}

/**
 * GET /api/groups — DB-backed hierarchy (replaces SharePoint folder browsing).
 *
 * Filter options (applied client-side; server only supports `?type=`):
 *   - type: 'corporate_group' | 'family_group' — narrow to one group type.
 *   - parentId: number | null — only return groups whose parent_id matches.
 *       Pass `null` to get top-level groups (corp groups + standalone families).
 *       Pass a number to get the family-group children of that corp group.
 *       Omit (undefined) to skip the parent filter.
 */
export interface UseGroupsFilter {
  type?: GroupType;
  parentId?: number | null;
}

export function useGroups(
  baseApiUrl: string,
  filter: UseGroupsFilter = {},
): AsyncState<Group[]> & { refetch: () => void } {
  const [state, setState] = useState<AsyncState<Group[]>>(initialState());
  const filterKey = JSON.stringify(filter);

  const load = useCallback(async () => {
    if (!baseApiUrl) return;
    const token = await getAccessToken();
    if (!token) return;
    setState({ data: null, loading: true, error: null });
    try {
      const params: Record<string, string> = {};
      if (filter.type) params.type = filter.type;
      const res = await axios.get<Group[]>(`${baseApiUrl}/api/groups`, {
        params,
        headers: { Authorization: `Bearer ${token}` },
      });
      let rows = res.data;
      if (filter.parentId !== undefined) {
        rows = rows.filter((g) => (g.parent_id ?? null) === filter.parentId);
      }
      setState({ data: rows, loading: false, error: null });
    } catch (err) {
      setState({ data: null, loading: false, error: errorMessage(err) });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseApiUrl, filterKey]);

  useEffect(() => { void load(); }, [load]);

  return { ...state, refetch: () => void load() };
}

/**
 * GET /api/job-records?group_id=
 */
export function useJobs(
  baseApiUrl: string,
  groupId: number | null,
): AsyncState<Job[]> & { refetch: () => void } {
  const [state, setState] = useState<AsyncState<Job[]>>(initialState());

  const load = useCallback(async () => {
    if (!baseApiUrl || groupId == null) {
      setState(initialState());
      return;
    }
    const token = await getAccessToken();
    if (!token) return;
    setState({ data: null, loading: true, error: null });
    try {
      const res = await axios.get<Job[]>(`${baseApiUrl}/api/job-records`, {
        params: { group_id: groupId },
        headers: { Authorization: `Bearer ${token}` },
      });
      setState({ data: res.data, loading: false, error: null });
    } catch (err) {
      setState({ data: null, loading: false, error: errorMessage(err) });
    }
  }, [baseApiUrl, groupId]);

  useEffect(() => { void load(); }, [load]);

  return { ...state, refetch: () => void load() };
}

/**
 * GET /api/job-entities?job_id=
 * Then enrich each row with the entity record (for entity_type_name) via /api/entities/:id.
 */
export function useEntities(
  baseApiUrl: string,
  jobId: number | null,
): AsyncState<Array<JobEntity & { entity: Entity | null }>> & { refetch: () => void } {
  const [state, setState] = useState<AsyncState<Array<JobEntity & { entity: Entity | null }>>>(initialState());

  const load = useCallback(async () => {
    if (!baseApiUrl || jobId == null) {
      setState(initialState());
      return;
    }
    const token = await getAccessToken();
    if (!token) return;
    setState({ data: null, loading: true, error: null });
    try {
      const linksRes = await axios.get<JobEntity[]>(`${baseApiUrl}/api/job-entities`, {
        params: { job_id: jobId },
        headers: { Authorization: `Bearer ${token}` },
      });
      const links = linksRes.data;
      const entities = await Promise.all(
        links.map((l) =>
          axios
            .get<Entity>(`${baseApiUrl}/api/entities/${l.entity_id}`, {
              headers: { Authorization: `Bearer ${token}` },
            })
            .then((r) => r.data)
            .catch(() => null),
        ),
      );
      const merged = links.map((l, i) => ({ ...l, entity: entities[i] }));
      setState({ data: merged, loading: false, error: null });
    } catch (err) {
      setState({ data: null, loading: false, error: errorMessage(err) });
    }
  }, [baseApiUrl, jobId]);

  useEffect(() => { void load(); }, [load]);

  return { ...state, refetch: () => void load() };
}

/**
 * GET /api/documents/folder/:itemId — SharePoint folder children.
 * Pass the entity's sharepoint_folder_id to list its documents.
 */
export function useDocuments(baseApiUrl: string, folderId: string | null): AsyncState<FolderItem[]> & { refetch: () => void } {
  const [state, setState] = useState<AsyncState<FolderItem[]>>(initialState());
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!baseApiUrl || !folderId) {
      setState(initialState());
      return;
    }
    let cancelled = false;
    (async () => {
      const token = await getAccessToken();
      if (!token || cancelled) return;
      setState({ data: null, loading: true, error: null });
      try {
        const res = await axios.get<FolderItem[]>(`${baseApiUrl}/api/documents/folder/${folderId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) setState({ data: res.data, loading: false, error: null });
      } catch (err) {
        if (!cancelled) setState({ data: null, loading: false, error: errorMessage(err) });
      }
    })();
    return () => { cancelled = true; };
  }, [baseApiUrl, folderId, tick]);

  return { ...state, refetch: () => setTick((t) => t + 1) };
}

/**
 * GET /api/documents/search?q= — flat full-text search across the SharePoint site.
 * Empty query returns []. Caller is expected to debounce.
 */
export function useDocumentSearch(
  baseApiUrl: string,
  query: string,
): AsyncState<SearchResult[]> & { refetch: () => void } {
  const [state, setState] = useState<AsyncState<SearchResult[]>>(initialState());
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!baseApiUrl) return;
    if (!query.trim()) {
      setState({ data: [], loading: false, error: null });
      return;
    }
    let cancelled = false;
    (async () => {
      const token = await getAccessToken();
      if (!token || cancelled) return;
      setState({ data: null, loading: true, error: null });
      try {
        const res = await axios.get<SearchResult[]>(`${baseApiUrl}/api/documents/search`, {
          params: { q: query },
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) setState({ data: res.data, loading: false, error: null });
      } catch (err) {
        if (!cancelled) setState({ data: null, loading: false, error: errorMessage(err) });
      }
    })();
    return () => { cancelled = true; };
  }, [baseApiUrl, query, tick]);

  return { ...state, refetch: () => setTick((t) => t + 1) };
}

/**
 * GET /api/documents/recent — most-recently-modified files from the document_index SQL table.
 * Used by the Documents tab to populate a default view when the search box is empty.
 */
export function useRecentDocuments(
  baseApiUrl: string,
  limit: number = 50,
): AsyncState<SearchResult[]> & { refetch: () => void } {
  const [state, setState] = useState<AsyncState<SearchResult[]>>(initialState());
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!baseApiUrl) return;
    let cancelled = false;
    (async () => {
      const token = await getAccessToken();
      if (!token || cancelled) return;
      setState({ data: null, loading: true, error: null });
      try {
        const res = await axios.get<SearchResult[]>(`${baseApiUrl}/api/documents/recent`, {
          params: { limit },
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) setState({ data: res.data, loading: false, error: null });
      } catch (err) {
        if (!cancelled) setState({ data: null, loading: false, error: errorMessage(err) });
      }
    })();
    return () => { cancelled = true; };
  }, [baseApiUrl, limit, tick]);

  return { ...state, refetch: () => setTick((t) => t + 1) };
}

/**
 * GET /api/tasks — paginated list. Returns the full envelope so callers see total/page.
 */
export function useTasks(
  baseApiUrl: string,
  filters: { jobId?: number; entityId?: number; status?: string } = {},
): AsyncState<TasksListResponse> {
  const [state, setState] = useState<AsyncState<TasksListResponse>>(initialState());
  const filterKey = JSON.stringify(filters);

  useEffect(() => {
    if (!baseApiUrl) return;
    let cancelled = false;

    (async () => {
      const token = await getAccessToken();
      if (!token || cancelled) return;
      setState({ data: null, loading: true, error: null });

      const params: Record<string, string | number> = {};
      if (filters.jobId != null) params.jobId = filters.jobId;
      if (filters.entityId != null) params.entityId = filters.entityId;
      if (filters.status) params.status = filters.status;

      try {
        const res = await axios.get<TasksListResponse>(`${baseApiUrl}/api/tasks`, {
          params,
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        // taskService.listTasks returns { tasks, total, page, pageSize }; tolerate a bare-array
        // response too in case paging is disabled.
        const payload = Array.isArray(res.data)
          ? { tasks: res.data as unknown as Task[], total: (res.data as unknown as Task[]).length, page: 1, pageSize: (res.data as unknown as Task[]).length }
          : res.data;
        setState({ data: payload, loading: false, error: null });
      } catch (err) {
        if (!cancelled) setState({ data: null, loading: false, error: errorMessage(err) });
      }
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseApiUrl, filterKey]);

  return state;
}

/**
 * GET /api/options/:table — generic lookup-table fetch (departments, job_types, etc.).
 * Returns active rows only (filter applied client-side; the API returns active+inactive).
 */
export function useOptions(
  baseApiUrl: string,
  table: LookupTable | null,
): AsyncState<LookupRow[]> {
  const [state, setState] = useState<AsyncState<LookupRow[]>>(initialState());

  useEffect(() => {
    if (!baseApiUrl || !table) {
      setState(initialState());
      return;
    }
    let cancelled = false;
    (async () => {
      const token = await getAccessToken();
      if (!token || cancelled) return;
      setState({ data: null, loading: true, error: null });
      try {
        const res = await axios.get<LookupRow[]>(`${baseApiUrl}/api/options/${table}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        const active = res.data.filter((r) => r.is_active !== false);
        setState({ data: active, loading: false, error: null });
      } catch (err) {
        if (!cancelled) setState({ data: null, loading: false, error: errorMessage(err) });
      }
    })();
    return () => { cancelled = true; };
  }, [baseApiUrl, table]);

  return state;
}

/**
 * GET /api/entities?search= — search existing entities by name (server-side LIKE filter).
 * Used by the "Link existing entity" tab in CreateEntityModal. Caller debounces.
 * Empty query returns [] without hitting the API.
 */
export function useEntitySearch(
  baseApiUrl: string,
  query: string,
): AsyncState<Entity[]> {
  const [state, setState] = useState<AsyncState<Entity[]>>(initialState());

  useEffect(() => {
    if (!baseApiUrl) return;
    const trimmed = query.trim();
    if (!trimmed) {
      setState({ data: [], loading: false, error: null });
      return;
    }
    let cancelled = false;
    (async () => {
      const token = await getAccessToken();
      if (!token || cancelled) return;
      setState({ data: null, loading: true, error: null });
      try {
        const res = await axios.get<Entity[]>(`${baseApiUrl}/api/entities`, {
          params: { search: trimmed },
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) setState({ data: res.data, loading: false, error: null });
      } catch (err) {
        if (!cancelled) setState({ data: null, loading: false, error: errorMessage(err) });
      }
    })();
    return () => { cancelled = true; };
  }, [baseApiUrl, query]);

  return state;
}
